import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listOrders } from '../../services/orders'
import { getUser, logout } from '../../services/helpers'
import MobileBottomNav from '../../components/MobileBottomNav'
import {
  CalendarIcon,
  TicketIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  TagIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'

const OrdersPage = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(null)
  const displayName = user?.name || user?.email || 'Attendee'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    setLoading(true)
    const res = await listOrders()
    if (res.status === 200) {
      setOrders(res.data || [])
    }
    setLoading(false)
  }

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true
    return order.status === filter
  })

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
      case 'TICKET_ISSUED':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'INITIATED':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'FAILED':
      case 'CANCELLED':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getOrderType = (order) => {
    return order.orderType || 'EVENT'
  }

  const tabs = [
    { id: 'events', label: 'Discover Events', icon: CalendarIcon, path: '/events' },
    { id: 'offers', label: 'Browse Offers', icon: TagIcon, path: '/offers' },
    { id: 'centers', label: 'Centers', icon: BuildingOffice2Icon, path: '/centers' },
    { id: 'tickets', label: 'My Tickets', icon: TicketIcon, path: '/account/tickets' },
    { id: 'orders', label: 'Order History', icon: ShoppingBagIcon, path: '/account/orders' },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/account/disputes' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/account/profile' }
  ]

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation - Hidden on mobile, shown on desktop */}
      <aside className={`hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const currentPath = window.location.pathname
              const isActive = tab.id === 'centers' 
                ? currentPath.startsWith('/centers')
                : currentPath === tab.path
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col pb-20 lg:pb-0">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-center px-4 h-16 sticky top-0 z-40">
          <span className="font-semibold text-slate-900">Order History</span>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Order History</h1>
              <p className="text-slate-600">Track all your ticket purchases and transactions</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { id: 'all', label: 'All Orders', count: orders.length },
                { id: 'TICKET_ISSUED', label: 'Completed', count: orders.filter(o => o.status === 'TICKET_ISSUED').length },
                { id: 'INITIATED', label: 'Pending', count: orders.filter(o => o.status === 'INITIATED').length },
                { id: 'FAILED', label: 'Failed', count: orders.filter(o => o.status === 'FAILED').length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setFilter(tab.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    filter === tab.id
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30'
                      : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  {tab.label} <span className="ml-2 opacity-75">({tab.count})</span>
                </button>
              ))}
            </div>

            {/* Orders List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                  <p className="mt-4 text-slate-600">Loading your orders...</p>
                </div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-20">
                <ShoppingBagIcon className="mx-auto h-24 w-24 text-slate-300" />
                <h3 className="mt-6 text-2xl font-semibold text-slate-900">No orders found</h3>
                <p className="mt-2 text-slate-600">
                  {filter === 'all' 
                    ? "You haven't made any purchases yet. Browse events and offers to get started!"
                    : `No ${filter.toLowerCase()} orders found.`}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Order Details</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredOrders.map(order => (
                        <tr key={order._id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-semibold text-slate-900">
                                {order.items && order.items[0]?.eventTitle 
                                  ? order.items[0].eventTitle 
                                  : order.items && order.items[0]?.offerTitle
                                  ? order.items[0].offerTitle
                                  : 'Order'}
                              </p>
                              <p className="text-xs text-slate-500 font-mono mt-1">#{order._id.slice(-8)}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                              getOrderType(order) === 'EVENT' 
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}>
                              {getOrderType(order) === 'EVENT' ? <CalendarIcon className="w-3 h-3" /> : <TagIcon className="w-3 h-3" />}
                              {getOrderType(order)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <ClockIcon className="w-4 h-4 text-slate-400" />
                              {new Date(order.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-bold text-slate-900">₦{order.total?.toLocaleString()}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getStatusColor(order.status)}`}>
                              {order.status === 'TICKET_ISSUED' || order.status === 'PAID' ? (
                                <CheckCircleIcon className="w-4 h-4" />
                              ) : order.status === 'INITIATED' ? (
                                <ClockIcon className="w-4 h-4" />
                              ) : (
                                <XCircleIcon className="w-4 h-4" />
                              )}
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <MobileBottomNav />
    </div>
  )
}

export default OrdersPage

